/**
 * Trust module hooks (v1.1-C4) — full real-data binding for the
 * /trust subpages. Activity, audit, violations, data-flows all read
 * from audit_logs + governance_incidents on the API.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type {
  ActivityEvent,
  ViolationPattern,
  DataFlowSummary,
  EventType,
  EventCategory,
  EventOutcome,
  Severity,
  Classification,
} from './mock-trust-activity';

interface ServerActivityEvent {
  id: string;
  sequenceNumber: number;
  timestamp: string;
  hash: string;
  previousHash: string | null;
  parentEventId: string | null;
  conversationId: string | null;
  workflowRunId: string | null;
  actorType: string;
  userId: string | null;
  agentId: string | null;
  workflowId: string | null;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  type: string;
  category: string;
  outcome: string;
  severity: string;
  summary: string;
  inputSnippet: string | null;
  outputSnippet: string | null;
  classifications: string[];
  frameworkControls: string[];
  durationMs: number | null;
  ipAddress: string | null;
  userAgent: string | null;
  sourceSystem: string | null;
  externalDestination: string | null;
  externalTier: string | null;
  bytesIn: number | null;
  bytesOut: number | null;
  metadata: Record<string, unknown>;
}

function adaptActivityEvent(s: ServerActivityEvent): ActivityEvent {
  return {
    id: s.id,
    sequenceNumber: s.sequenceNumber,
    timestamp: s.timestamp,
    hash: s.hash,
    previousHash: s.previousHash,
    parentEventId: s.parentEventId,
    conversationId: s.conversationId,
    workflowRunId: s.workflowRunId,
    actorType: s.actorType as ActivityEvent['actorType'],
    userId: s.userId,
    agentId: s.agentId,
    workflowId: s.workflowId,
    targetType: s.targetType as ActivityEvent['targetType'],
    targetId: s.targetId,
    targetLabel: s.targetLabel,
    type: s.type as EventType,
    category: s.category as EventCategory,
    outcome: s.outcome as EventOutcome,
    severity: s.severity as Severity,
    summary: s.summary,
    inputSnippet: s.inputSnippet,
    outputSnippet: s.outputSnippet,
    classifications: s.classifications as Classification[],
    frameworkControls: s.frameworkControls,
    durationMs: s.durationMs,
    ipAddress: s.ipAddress,
    userAgent: s.userAgent,
    sourceSystem: s.sourceSystem,
    externalDestination: s.externalDestination,
    externalTier: s.externalTier as ActivityEvent['externalTier'],
    bytesIn: s.bytesIn,
    bytesOut: s.bytesOut,
    metadata: s.metadata,
  };
}

export function useTrustActivity(limit = 200) {
  return useQuery<{ events: ServerActivityEvent[] }, Error, ActivityEvent[]>({
    queryKey: ['trust-module', 'activity', limit],
    queryFn: () =>
      apiClient.get<{ events: ServerActivityEvent[] }>(
        `/trust-module/activity?limit=${limit}`,
      ),
    select: (r) => r.events.map(adaptActivityEvent),
  });
}

export interface ServerAuditEntry {
  id: string;
  createdAt: string;
  userId: string | null;
  userEmail: string | null;
  action: string;
  category: string | null;
  outcome: string | null;
  resourceType: string;
  resourceId: string | null;
  ipAddress: string | null;
  sequenceNumber: number | null;
  recordHash: string | null;
  previousHash: string | null;
  details: string | null;
  metadata: Record<string, unknown> | null;
}

export function useTrustAudit(cursor?: string) {
  return useQuery<
    { entries: ServerAuditEntry[]; nextCursor: string | null },
    Error
  >({
    queryKey: ['trust-module', 'audit', cursor],
    queryFn: () =>
      apiClient.get<{ entries: ServerAuditEntry[]; nextCursor: string | null }>(
        `/trust-module/audit${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      ),
  });
}

interface ServerViolation {
  id: string;
  title: string;
  description: string;
  severity: string;
  category: string;
  eventCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  exemplarEventId: string | null;
  affectedUsers: number;
  affectedAssets: number;
  detectionRule: string;
  status: string;
}

function adaptViolation(s: ServerViolation): ViolationPattern {
  // Note: ServerViolation has fewer fields than the mock VM. Fields not
  // present on the server (patternType, relatedEventIds, framework
  // controls per-violation, etc.) are filled with sensible defaults so
  // the existing render code doesn't crash.
  // We cast through unknown because we are intentionally returning a
  // partial-but-render-safe shape.
  return ({
    id: s.id,
    title: s.title,
    description: s.description,
    detectedAt: s.firstSeenAt,
    severity: s.severity as ViolationPattern['severity'],
    patternType: 'single_event' as ViolationPattern['patternType'],
    relatedEventIds: [],
    affectedUserId: null,
    affectedAgentId: null,
    frameworkControls: [],
    recommendedAction: '',
    status: s.status as ViolationPattern['status'],
    resolvedBy: null,
    resolvedAt: null,
    resolvedNote: null,
    // legacy / extended fields tolerated by some renderers:
    category: s.category,
    eventCount: s.eventCount,
    firstSeenAt: s.firstSeenAt,
    lastSeenAt: s.lastSeenAt,
    exemplarEventId: s.exemplarEventId ?? '',
    affectedUsers: s.affectedUsers,
    affectedAssets: s.affectedAssets,
    detectionRule: s.detectionRule,
  } as unknown) as ViolationPattern;
}

export function useTrustViolations(limit = 200) {
  return useQuery<
    { patterns: ServerViolation[] },
    Error,
    ViolationPattern[]
  >({
    queryKey: ['trust-module', 'violations', limit],
    queryFn: () =>
      apiClient.get<{ patterns: ServerViolation[] }>(
        `/trust-module/violations?limit=${limit}`,
      ),
    select: (r) => r.patterns.map(adaptViolation),
  });
}

interface ServerDataFlow {
  destination: string;
  tier: string;
  requestCount: number;
  bytesOut: number;
  bytesIn: number;
  lastSeenAt: string;
  classifications: string[];
}

function adaptDataFlow(s: ServerDataFlow): DataFlowSummary {
  const tierKind = (s.tier === 'tier_1_iq' || s.tier === 'tier_2_ext' || s.tier === 'tier_3_forge' || s.tier === 'public_web' || s.tier === 'internal')
    ? (s.tier as DataFlowSummary['destination']['kind'])
    : 'tier_2_ext';
  return {
    id: s.destination,
    source: { kind: 'agent', id: 'system', label: 'System' },
    destination: {
      kind: tierKind,
      host: s.destination,
      label: s.destination,
    },
    classifications: s.classifications as Classification[],
    eventCount24h: s.requestCount,
    bytesOut24h: s.bytesOut,
    bytesIn24h: s.bytesIn,
    lastActivityAt: s.lastSeenAt,
    approved: false,
    firstSeen: s.lastSeenAt,
    anomalyScore: 0,
    sampleEventIds: [],
  };
}

export function useTrustDataFlows() {
  return useQuery<{ flows: ServerDataFlow[] }, Error, DataFlowSummary[]>({
    queryKey: ['trust-module', 'data-flows'],
    queryFn: () =>
      apiClient.get<{ flows: ServerDataFlow[] }>('/trust-module/data-flows'),
    select: (r) => r.flows.map(adaptDataFlow),
  });
}
